import { useCallback } from "react";
import { useFormContext } from "react-hook-form";
import { useConditionBuilderContext } from "@/components/ConditionBuilder/context/ConditionBuilderContext";
import { ConditionBuilderConfig } from "@/components/ConditionBuilder/types";

/**
 * Combines the RHF form API (valueGetter/onChange) with the
 * ConditionBuilder configuration from context.
 *
 * Mirrors the project's useFormApiCtx surface so existing consumer code that
 * does `valueGetter(name)` and `onChange(name, { value })` keeps working.
 */
export const useConditionBuilder = (): {
  valueGetter: (name: string) => unknown;
  onChange: (name: string, payload: { value: unknown }) => void;
  config: ConditionBuilderConfig;
} => {
  const methods = useFormContext();
  const { config } = useConditionBuilderContext();

  const valueGetter = useCallback(
    (name: string) => methods.getValues(name as never),
    [methods],
  );

  const onChange = useCallback(
    (name: string, payload: { value: unknown }) => {
      methods.setValue(name as never, payload.value as never, {
        shouldDirty: true,
        shouldTouch: true,
        shouldValidate: true,
      });
    },
    [methods],
  );

  return { valueGetter, onChange, config };
};
